/*
 * Copyright (c) Nexign, JSC, 2022.
 */
/**
 *
 *
 * @author Yakov Gushchin
 * @author Ilya Babanov
 * @version 1.22.0
 * @since 1.19.0
 */

import { ColumnWithStrictAccessor } from 'react-table';

import { EGTMBasketSectionType } from '../analytics/GoogleTagManager/GTMOperations/GTMBasketOperations';
import type { ILogBusinessEventPayload } from '../logging/loggingTypes';
import { AccessComponentKeyEnum } from '../role/constants/guard/guardConstants';
import type { ITableMetaResponse } from './api/types';

export interface ITableInitialise {
    preset?: {
        filterId?: string | null;
        filter?: ITableFilter;
        sort?: ITableSort;
    },
    meta?: { url: string };
    list?: {
        // В Cookie хранится значения кол-ва загружаемых строк за раз. При включенном флаге кол-во - константа 24
        disableCheckOnDensity?: boolean;
        url: string;
        log?: ILogBusinessEventPayload;
        isTemplate?: boolean;
    };
    filterAndSort?: {
        url: string;
        preset?: string;
        suggest?: string;
    };
    basket?: {
        checkbox: string;
        list: string;
        gtm?: {
            loadList: EGTMBasketSectionType;
        };
    };
    resize?: { url: string; };
    columnSettings?: { url: string };
    labels?: {
        url: string;
        fieldName: string;
    };
    totalCount?: {
      url: string;
    };
}

export type TTableColumnCell<D extends object> = ColumnWithStrictAccessor<D> & {
    align?: 'left' | 'right';
    sticky?: 'left';
    disableResizing?: boolean;
    rowCount?: number;
};

export type TTableColumnConfig<D extends object> = { [key: string]: TTableColumnCell<D> }

export type TableMeta = Pick<ITableMetaResponse, 'fields'> & {
    filter?: ITableFilter;
    sort?: ITableSort;
    facets?: ITableFacets;
    // export
    exportExtensions?: string[]
    // basket
    basketAllIn?: boolean;
}

export type ITableFilter = { [key: string]: any };

export enum ETableSort {
    asc = 'ASC',
    default = 'DEFAULT',
    desc = 'DESC',
}

export type ITableSort = {
    direction: ETableSort
    property: string;
};

export type ITableFacets = { [key: string]: any };


export interface ITableData<Data extends object> {
    elements: Data[];
    count: number;
    // basket
    basketAllIn: boolean;
    searchAfterValues: string[] | null;
    // subscribers in roaming
    subscribersInRoamingCount?: number;
    subscribersInRoamingWithFilterCount?: number;
    filterSubscribersInRoaming?: boolean;
}

export interface ITableBasketData extends ITableData<ITableBasketItem> {
    params?: any;
}

export interface ITableBasketItem {
    id: number;
    label: string;
}

/* Footer Buttons */

export enum ETableButtonsMode {
    disableAllExceptInArray = 'disableAllExceptInArray',
}

export interface ITableButtonsMode {
    mode: ETableButtonsMode | null;
    buttonsForCheckOnMode: AccessComponentKeyEnum[];
}

/* Labels */

export interface ITableLabelItem {
    value: number;
    label: string;
    color: string;
}

export type ITableLabelsData = ITableLabelItem[];
